import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaWallet,
  FaArrowLeft,
  FaArrowUp,
  FaArrowDown,
  FaFilter,
  FaCalendarAlt,
  FaRupeeSign,
  FaReceipt,
  FaEye,
  FaDownload,
  FaSearch,
  FaImage
} from 'react-icons/fa';
import DriverLayout from '../../layouts/DriverLayout';
import {
  getDriverWallet,
  getDriverTransactions,
  reset as resetWallet
} from '../../features/wallet/walletSlice';

const WalletHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { driver: driverAuth } = useSelector((state) => state.driverAuth);
  const { wallet, transactions, isLoading, isError, message } = useSelector((state) => state.wallet);

  const [filters, setFilters] = useState({
    type: 'all',
    dateRange: '30',
    search: ''
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Get driver ID
  const driverId = driverAuth?._id || driverAuth?.driver?._id;

  useEffect(() => {
    if (driverId) {
      if (!wallet) {
        dispatch(getDriverWallet(driverId));
      }
      dispatch(getDriverTransactions({ 
        driverId, 
        page: currentPage, 
        limit: itemsPerPage 
      }));
    }
  }, [dispatch, driverId, currentPage, itemsPerPage, wallet]);

  useEffect(() => {
    if (isError && message) {
      toast.error(message);
      dispatch(resetWallet());
    }
  }, [isError, message, dispatch]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'ride_earnings':
      case 'ride_earning': // Backend uses singular form
      case 'earning':
      case 'online_ride_earning':
        return <FaArrowUp className="text-green-500" />;
      case 'commission_deduction':
      case 'admin_commission_deduction':
      case 'vendor_commission_deduction':
      case 'commission_payment':
      case 'commission_cut':
      case 'cod_ride_deduction':
      case 'cod_commission_deduction':
      case 'cod_admin_commission_deduction':
      case 'cod_vendor_commission_deduction':
      case 'cod_commission_settlement':
        return <FaArrowDown className="text-red-500" />;
      case 'withdrawal':
      case 'withdrawal_approved':
        return <FaArrowUp className="text-blue-500" />;
      case 'deposit':
      case 'wallet_topup':
        return <FaArrowDown className="text-blue-500" />;
      default:
        return <FaReceipt className="text-gray-500" />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'ride_earnings':
      case 'ride_earning': // Backend uses singular form
      case 'earning':
      case 'online_ride_earning':
      case 'deposit':
      case 'wallet_topup':
        return 'text-green-600';
      case 'commission_deduction':
      case 'admin_commission_deduction':
      case 'vendor_commission_deduction':
      case 'commission_payment':
      case 'commission_cut':
      case 'cod_ride_deduction':
      case 'cod_commission_deduction':
      case 'cod_admin_commission_deduction':
      case 'cod_vendor_commission_deduction':
      case 'cod_commission_settlement':
      case 'withdrawal':
      case 'withdrawal_approved':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getTransactionTitle = (type) => {
    switch (type) {
      case 'ride_earnings':
      case 'ride_earning': // Backend uses singular form
      case 'earning':
        return 'Ride Earnings';
      case 'online_ride_earning':
        return 'Online Ride Earning';
      case 'commission_deduction':
        return 'Ride Commission';
      case 'admin_commission_deduction':
        return 'Ride Commission';
      case 'vendor_commission_deduction':
        return 'Ride Commission';
      case 'commission_payment':
        return 'Commission Payment';
      case 'commission_cut':
        return 'Commission Deducted';
      case 'cod_ride_deduction':
        return 'COD Ride Deduction';
      case 'cod_commission_deduction':
        return 'Ride Commission';
      case 'cod_admin_commission_deduction':
        return 'Ride Commission';
      case 'cod_vendor_commission_deduction':
        return 'Ride Commission';
      case 'cod_commission_settlement':
        return 'Commission Settlement';
      case 'withdrawal':
      case 'withdrawal_approved':
        return 'Withdrawal';
      case 'deposit':
      case 'wallet_topup':
        return 'Wallet Top-up';
      default:
        return 'Transaction';
    }
  };

  // Helper function to categorize transaction types
  const getTransactionCategory = (type) => {
    if (['ride_earnings', 'ride_earning', 'earning', 'online_ride_earning'].includes(type)) {
      return 'earnings';
    }
    if (['commission_deduction', 'admin_commission_deduction', 'vendor_commission_deduction',
         'commission_payment', 'commission_cut', 'cod_commission_deduction',
         'cod_admin_commission_deduction', 'cod_vendor_commission_deduction',
         'cod_commission_settlement'].includes(type)) {
      return 'commission';
    }
    if (['withdrawal', 'withdrawal_approved'].includes(type)) {
      return 'withdrawal';
    }
    if (['deposit', 'wallet_topup'].includes(type)) {
      return 'deposit';
    }
    return 'other';
  };

  // Use server-side transactions if available, otherwise fall back to wallet transactions
  const transactionsToUse = transactions && transactions.length > 0 ? transactions : (wallet?.transactions || []);

  // Prefer custom booking number if provided by API
  const getDisplayBookingId = (tx) => {
    const candidate = tx.bookingNumber || tx.customBookingId || tx.bookingId;
    if (!candidate) return '';
    const isMongoId = typeof candidate === 'string' && /^[a-fA-F0-9]{24}$/.test(candidate);
    return isMongoId ? candidate : candidate;
  };

  // Replace ObjectId in description with custom booking number if available
  const formatTransactionDescription = (tx) => {
    const original = tx.description || 'No description';
    const rawId = tx.bookingId || '';
    const bookingNumber = tx.bookingNumber || tx.customBookingId || '';

    // Special handling for settlement entries to avoid mislabeling them as COD commission for a booking
    if (tx.type === 'cod_commission_settlement') {
      return 'Commission settlement from previous COD rides';
    }

    const commissionTypes = new Set([
      'commission_deduction',
      'admin_commission_deduction',
      'vendor_commission_deduction',
      'commission_payment',
      'commission_cut',
      'cod_commission_deduction',
      'cod_admin_commission_deduction',
      'cod_vendor_commission_deduction',
    ]);

    if (commissionTypes.has(tx.type)) {
      const isCOD = tx.type.startsWith('cod_');
      const displayId = getDisplayBookingId(tx);
      if (displayId) {
        return `Ride commission for ${isCOD ? 'COD' : 'online'} booking #${displayId}`;
      }
      return `Ride commission for ${isCOD ? 'COD' : 'online'} booking`;
    }

    if (!bookingNumber) return original;

    let updated = original;
    if (rawId && typeof rawId === 'string') {
      updated = updated.replace(rawId, bookingNumber);
    }
    updated = updated.replace(/(booking\s*#)[a-fA-F0-9]{24}/i, `$1${bookingNumber}`);
    return updated;
  };

  // Combine admin and vendor commission entries for the same booking into a single entry
  const combinedTransactions = (() => {
    const COMMISSION_TYPES = new Set([
      'admin_commission_deduction',
      'vendor_commission_deduction',
      'cod_admin_commission_deduction',
      'cod_vendor_commission_deduction',
    ]);

    const groups = new Map();
    const others = [];

    for (const tx of transactionsToUse) {
      const isCommission = COMMISSION_TYPES.has(tx.type);
      const hasBooking = !!tx.bookingId;

      if (isCommission && hasBooking) {
        const isCOD = tx.type.startsWith('cod_');
        const key = `${tx.bookingId}|${isCOD ? 'cod' : 'online'}`;
        const existing = groups.get(key);

        if (existing) {
          const adminAmt = (existing.adminCommissionAmount || 0) + (tx.type.includes('admin') ? (tx.amount || 0) : 0);
          const vendorAmt = (existing.vendorCommissionAmount || 0) + (tx.type.includes('vendor') ? (tx.amount || 0) : 0);

          groups.set(key, {
            ...existing,
            amount: (existing.amount || 0) + (tx.amount || 0),
            date: new Date(existing.date) > new Date(tx.date) ? existing.date : tx.date,
            adminCommissionAmount: adminAmt,
            vendorCommissionAmount: vendorAmt,
          });
        } else {
          groups.set(key, {
            ...tx,
            type: isCOD ? 'cod_commission_deduction' : 'commission_deduction',
            amount: tx.amount || 0,
            adminCommissionAmount: tx.type.includes('admin') ? (tx.amount || 0) : (tx.adminCommissionAmount || 0) || 0,
            vendorCommissionAmount: tx.type.includes('vendor') ? (tx.amount || 0) : (tx.vendorCommissionAmount || 0) || 0,
            isCombinedCommission: false,
          });
        }
      } else {
        others.push(tx);
      }
    }

    const combined = [];
    for (const [key, value] of groups.entries()) {
      const hasAdmin = (value.adminCommissionAmount || 0) > 0;
      const hasVendor = (value.vendorCommissionAmount || 0) > 0;

      if (hasAdmin && hasVendor) {
        combined.push({
          ...value,
          isCombinedCommission: true,
          description:
            value.description && value.description.toLowerCase().includes('booking #')
              ? value.description
              : `Ride commission for ${key.includes('|cod') ? 'COD' : 'online'} booking #${getDisplayBookingId(value)}`,
        });
      } else {
        const [bookingId, flavor] = key.split('|');
        const isCOD = flavor === 'cod';
        transactionsToUse.forEach((t) => {
          if (COMMISSION_TYPES.has(t.type) && t.bookingId === bookingId && (t.type.startsWith('cod_') === isCOD)) {
            others.push(t);
          }
        });
      }
    }

    return [...others, ...combined];
  })();

  // Filter transactions based on current filters (use combined list)
  const filteredTransactions = combinedTransactions.filter(transaction => {
    // Type filter - use categories instead of exact types
    if (filters.type !== 'all') {
      const category = getTransactionCategory(transaction.type);
      if (category !== filters.type) {
        return false;
      }
    }

    // Date range filter
    if (filters.dateRange !== 'all') {
      const transactionDate = new Date(transaction.date);
      const daysAgo = parseInt(filters.dateRange);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
      
      if (transactionDate < cutoffDate) {
        return false;
      }
    }

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const description = (transaction.description || '').toLowerCase();
      const amount = transaction.amount.toString();
      
      if (!description.includes(searchTerm) && !amount.includes(searchTerm)) {
        return false;
      }
    }

    return true;
  });

  // Sort transactions by date (newest first)
  const sortedTransactions = [...filteredTransactions].sort((a, b) => 
    new Date(b.date) - new Date(a.date)
  );

  // Pagination
  const totalItems = sortedTransactions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTransactions = sortedTransactions.slice(startIndex, startIndex + itemsPerPage);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  if (isLoading && !wallet) {
    return (
      <DriverLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
          <div className="ml-4 text-gray-700 font-medium">Loading wallet history...</div>
        </div>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/driver/dashboard')}
              className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <FaArrowLeft className="text-gray-600" />
            </button>
            <div className="flex items-center">
              <FaWallet className="text-primary text-2xl mr-3" />
              <h1 className="text-2xl font-bold text-gray-800">Wallet History</h1>
            </div>
          </div>

          <button
            onClick={() => navigate('/driver/wallet/withdraw')}
            className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
          >
            <FaArrowUp className="mr-2" />
            Withdraw
          </button>
        </div>

        {/* Balance Summary */}
        <h2 className="text-xl font-semibold text-gray-800 mb-3">My Wallet</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Current Balance</p>
                <p className="text-2xl font-bold">
                  {(() => {
                    const bal = Number(wallet?.balance ?? wallet?.withdrawableBalance ?? 0);
                    return formatCurrency(bal);
                  })()}
                </p>
              </div>
              <FaRupeeSign className="text-3xl text-green-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Total Commission Sent</p>
                <p className="text-2xl font-bold">
                  {(() => {
                    // Calculate commission paid from transactions if not available
                    let commissionPaid = wallet?.calculatedCommission || wallet?.totalCommissionPaid || 0;

                    if (commissionPaid === 0 && wallet?.transactions) {
                      commissionPaid = wallet.transactions
                        .filter(t =>
                          t.type === 'commission_deduction' ||
                          t.type === 'admin_commission_deduction' ||
                          t.type === 'vendor_commission_deduction' ||
                          t.type === 'commission_payment' ||
                          t.type === 'commission_cut' ||
                          t.type === 'cod_commission_deduction' ||
                          t.type === 'cod_admin_commission_deduction' ||
                          t.type === 'cod_vendor_commission_deduction' ||
                          t.type === 'cod_commission_settlement'
                        )
                        .reduce((sum, t) => sum + (t.amount || 0), 0);
                    }

                    return formatCurrency(commissionPaid);
                  })()}
                </p>
              </div>
              <FaArrowDown className="text-3xl text-orange-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Total Withdrawals</p>
                <p className="text-2xl font-bold">{formatCurrency(wallet?.totalWithdrawals || 0)}</p>
              </div>
              <FaArrowDown className="text-3xl text-red-200" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Transaction Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transaction Type
              </label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="all">All Transactions</option>
                <option value="earnings">Ride Earnings</option>
                <option value="commission">Commission</option>
                <option value="withdrawal">Withdrawals</option>
                <option value="deposit">Deposits</option>
              </select>
            </div>

            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Range
              </label>
              <select
                value={filters.dateRange}
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 3 months</option>
                <option value="365">Last year</option>
                <option value="all">All time</option>
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Search transactions..."
                />
              </div>
            </div>

            {/* Export Button */}
            <div className="flex items-end">
              <button
                onClick={() => {
                  // Implement export functionality
                  toast.info('Export feature coming soon!');
                }}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                <FaDownload className="mr-2" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Transactions List */}
        <div className="bg-white rounded-xl shadow-md">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                Transaction History ({totalItems} transactions)
              </h2>
              {filters.type !== 'all' || filters.search || filters.dateRange !== '30' ? (
                <button
                  onClick={() => {
                    setFilters({ type: 'all', dateRange: '30', search: '' });
                    setCurrentPage(1);
                  }}
                  className="text-primary hover:text-primary-dark text-sm font-medium"
                >
                  Clear Filters
                </button>
              ) : null}
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {paginatedTransactions.length > 0 ? (
              paginatedTransactions.map((transaction, index) => (
                <div key={index} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="p-2 rounded-full bg-gray-100 mr-4">
                        {getTransactionIcon(transaction.type)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">
                          {transaction.isCombinedCommission ? 'Ride Commission' : getTransactionTitle(transaction.type)}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatTransactionDescription(transaction)}
                        </p>
                        {(transaction.bookingNumber || transaction.customBookingId || transaction.bookingId) && (
                          <p className="text-xs text-blue-600 font-medium">
                            Booking ID: {getDisplayBookingId(transaction)}
                          </p>
                        )}
                        {transaction.referenceId && (
                          <p className="text-xs text-blue-600 font-medium">
                            Reference ID: {transaction.referenceId}
                          </p>
                        )}
                        {transaction.paymentProofImage && (
                          <div className="flex items-center mt-1">
                            <FaImage className="text-green-600 text-xs mr-1" />
                            <button
                              onClick={() => window.open(transaction.paymentProofImage, '_blank')}
                              className="text-xs text-green-600 hover:text-green-700 font-medium"
                            >
                              View Payment Proof
                            </button>
                          </div>
                        )}
                        <p className="text-xs text-gray-400">
                          {formatDate(transaction.date)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold text-lg ${getTransactionColor(transaction.type)}`}>
                        {transaction.type === 'commission_deduction' ||
                         transaction.type === 'admin_commission_deduction' ||
                         transaction.type === 'vendor_commission_deduction' ||
                         transaction.type === 'commission_cut' ||
                         transaction.type === 'cod_ride_deduction' ||
                         transaction.type === 'cod_commission_deduction' ||
                         transaction.type === 'cod_admin_commission_deduction' ||
                         transaction.type === 'cod_vendor_commission_deduction' ||
                         transaction.type === 'cod_commission_settlement' ||
                         transaction.type === 'withdrawal' ||
                         transaction.type === 'withdrawal_approved' ? '-' : '+'}
                        {formatCurrency(Math.abs(transaction.amount))}
                      </p>
                      {transaction.balanceAfter !== undefined && (
                        <p className="text-sm text-gray-500">
                          Balance: {formatCurrency(transaction.balanceAfter)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-12 text-center">
                <FaReceipt className="text-gray-300 text-4xl mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-500 mb-2">No transactions found</h3>
                <p className="text-gray-400">
                  {filters.type !== 'all' || filters.search || filters.dateRange !== '30'
                    ? 'Try adjusting your filters to see more transactions.'
                    : 'Start completing rides to see your transaction history here.'
                  }
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-700">
                  Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, totalItems)} of{' '}
                  {totalItems} transactions
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNumber = Math.max(1, Math.min(currentPage - 2, totalPages - 4)) + i;
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`px-3 py-1 border rounded-md text-sm font-medium ${
                          pageNumber === currentPage
                            ? 'bg-primary text-white border-primary'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DriverLayout>
  );
};

export default WalletHistory;
